import { makeAutoObservable } from 'mobx';
import { Chat } from '../models/chat';
import { MyOrder } from '../models/order';
import { User } from '../models/user';
import chatService from '../services/chat';
import orderService from '../services/order';
import userService from '../services/user';
import currencyService from '../services/currency';
import productService from '../services/product';
import paymentService from '../services/payment';
import { CurrencyDayInfo } from '../models/currency';
import { Product } from '../models/product';
import { TaskInfo } from '../models/task';
import { DayPromoCode } from '../models/payment';


export default class AppStore {
    chats = [] as Chat[];
    myOrders = [] as MyOrder[];
    user = {} as User;
    currencyInfo = [] as CurrencyDayInfo[];
    products = [] as Product[];
    tasksInfo = null as TaskInfo | null;

    errorNum = 0;
    errors = {} as {[key: number]: {type: "error" | "notify", text: string}};
    paymentWindowOpened = false;
    paymentWindowAutoPromoCode = "";
    chatsUpdateProcess = false;
    myOrdersUpdateProcess = false;

    dayPromoCode = {} as DayPromoCode;

    processInvoice = "";
    checkInvoiceId = null as null | number;

    constructor() {
        makeAutoObservable(this, {}, {'autoBind': true});
    };

    async initApp() {
        this.getUser();
        this.getChats();
        this.getMyOrders();
        this.getCurrencyInfo();
        this.getProducts();
        this.getDayPromoCode();
    };

    openPaymentWindow(promoCode: string = "") {
        this.paymentWindowOpened = true;
        this.paymentWindowAutoPromoCode = promoCode;
    };

    closePaymentWindow() {
        this.paymentWindowOpened = false;
        this.paymentWindowAutoPromoCode = "";
    };

    addSystemMessage(type: "error" | "notify", message: string) {
        this.errors = {...this.errors, [this.errorNum]: {type: type, text: message}};
        this.errorNum += 1;

        const filteredObj = Object.fromEntries(
            Object.entries(this.errors) // Преобразуем объект в массив пар [ключ, значение]
            .sort((a, b) => Number(b[0]) - Number(a[0])) // Сортируем по убыванию ключей
            .slice(0, 3) // Берем три первых элемента (с самыми большими ключами)
            .sort((a, b) => Number(a[0]) - Number(b[0])) // Сортируем три выбранных пары по возрастанию ключей
        );
        this.errors = filteredObj;
    }

    addError(error: string) {
        const tg = window.Telegram.WebApp;
        tg.HapticFeedback.notificationOccurred("error");
        this.addSystemMessage("error", error);
    }

    addNotify(notify: string) {
        const tg = window.Telegram.WebApp;
        tg.HapticFeedback.notificationOccurred("success");
        this.addSystemMessage("notify", notify);
    }

    deleteError(key: number) {
        let curErrors = {...this.errors};
        delete curErrors[key];
        this.errors = {...curErrors};
    }

    clearErrors() {
        this.errors = {} as {[key: number]: {type: "error" | "notify", text: string}};
    }

    async getChats() {
        this.chatsUpdateProcess = true;
        
        try {
            const response = await chatService.get();

            if (response.status === 200) {
                this.chats = response.data.chats;
            };
        } catch {};

        this.chatsUpdateProcess = false;
    };

    async getMyOrders() {
        this.myOrdersUpdateProcess = true;
        
        try {
            const response = await orderService.get_my();

            if (response.status === 200) {
                this.myOrders = response.data.orders;
            };
        } catch {};

        this.myOrdersUpdateProcess = false;
    };

    async getUser() {
        const response = await userService.get();

        if (response.status === 200) {
            this.user = response.data;
        };
    };

    async getProducts() {
        const response = await productService.get();

        if (response.status === 200) {
            this.products = response.data.products;
        };
    };

    async getDayPromoCode() {
        const response = await paymentService.getDayPromoCode();

        if (response.status === 200) {
            this.dayPromoCode = response.data;
        };
    };

    async getCurrencyInfo() {
        const response = await currencyService.get();

        if (response.status === 200) {
            this.currencyInfo = response.data.info;
        };
    };

    // вынести логику из стора
    async createInvoice(productId: string, systemName: string) {
        const tg = window.Telegram.WebApp;
        tg.HapticFeedback.impactOccurred("light");
        
        this.processInvoice = `${productId}${systemName}`;

        const paymentError = () => {
            this.addError("При инициализации оплаты произошла ошибка, попробуйте позже или обратитесь в поддержку");
        };

        try {
            const response = await paymentService.createInvoice(productId, systemName);
            const invoiceLink = response.data.invoice_link;

            if (!invoiceLink) {
                paymentError();
                return;
            };

            if (systemName === "telegram") {
                tg.openInvoice(invoiceLink, (status) => {
                    if (status === 'paid' || status === 'pending') {
                        this.checkInvoiceId = response.data.invoice_id;
                    };
                });
            } else {
                tg.openLink(invoiceLink);

                setTimeout(() => {
                    this.checkInvoiceId = response.data.invoice_id;
                }, 1000);
            };

            this.processInvoice = "";
        } catch {
            paymentError();
        };
    };

    // вынести логику из стора
    async createDepositInvoice(deposit: number, promoCode: string | null) {
        const tg = window.Telegram.WebApp;
        tg.HapticFeedback.impactOccurred("light");
        
        this.processInvoice = "deposit";

        const baseError = (error: string) => {
            this.addError(error);
            this.processInvoice = "";
        };

        const paymentError = () => {
            baseError("При инициализации оплаты произошла ошибка, попробуйте позже или обратитесь в поддержку");
        }

        if (deposit < 10) {
            baseError("Сумма пополнения не может быть ниже 10 промокоинов");
            return;
        }

        try {
            const response = await paymentService.createDepositInvoice(deposit, promoCode);

            const invoiceLink = response.data.invoice_link;

            if (!invoiceLink) {
                paymentError();
                return;
            };

            tg.openLink(invoiceLink);

            setTimeout(() => {
                this.checkInvoiceId = response.data.invoice_id;
            }, 1000);
        } catch (error: any) {
            const errorMessage = error.response.data.error.message_ru || "Ошибка применения промокода";
            this.addError(errorMessage);
        } finally {
            this.processInvoice = "";
        }
    };
};